import type { BuilderWorkspace } from './types';
import type { Market } from '../../domain/location/types';
import { getT } from '../../i18n';

export interface BuilderWorkspaceMeta {
  id: BuilderWorkspace;
  label: string;
  taskTitle: string;
  nextHint: string;
  next: BuilderWorkspace | null;
  nextLabel: string | null;
}

export function getBaseWorkspaces(market: Market): BuilderWorkspaceMeta[] {
  const t = getT(market);

  return [
    {
      id: 'design',
      label: t.workspace_designLabel,
      taskTitle: t.workspace_designTask,
      nextHint: t.workspace_designHint,
      next: 'shopping',
      nextLabel: t.workspace_designNext,
    },
    {
      id: 'shopping',
      label: t.workspace_shoppingLabel,
      taskTitle: t.workspace_shoppingTask,
      nextHint: t.workspace_shoppingHint,
      next: 'cutlist',
      nextLabel: t.workspace_shoppingNext,
    },
    {
      id: 'cutlist',
      label: t.workspace_cutlistLabel,
      taskTitle: t.workspace_cutlistTask,
      nextHint: t.workspace_cutlistHint,
      next: 'pricing',
      nextLabel: t.workspace_cutlistNext,
    },
    {
      id: 'pricing',
      label: t.workspace_pricingLabel,
      taskTitle: t.workspace_pricingTask,
      nextHint: t.workspace_pricingHint,
      next: 'build',
      nextLabel: t.workspace_pricingNext,
    },
    {
      id: 'build',
      label: t.workspace_buildLabel,
      taskTitle: t.workspace_buildTask,
      nextHint: t.workspace_buildHint,
      next: 'rules',
      nextLabel: t.workspace_buildNext,
    },
    {
      id: 'rules',
      label: t.rules_tabLabel,
      taskTitle: t.workspace_rulesTask,
      nextHint: t.workspace_rulesHint,
      next: null,
      nextLabel: null,
    },
  ];
}

// Ids only: they are the same in every market. Labels always come from
// getBaseWorkspaces(market) — a module-level 'se' list was the fallback that
// let a caller without a market render Swedish tabs on intl.
export const VALID_WORKSPACES = new Set<BuilderWorkspace>(
  getBaseWorkspaces('se').map((workspace) => workspace.id),
);

export function parseWorkspace(value: string | null): BuilderWorkspace {
  if (value && VALID_WORKSPACES.has(value as BuilderWorkspace)) {
    return value as BuilderWorkspace;
  }

  return 'design';
}

export function getWorkspaceMeta(workspace: BuilderWorkspace, market: Market): BuilderWorkspaceMeta {
  const workspaces = getBaseWorkspaces(market);
  return workspaces.find((item) => item.id === workspace) ?? workspaces[0];
}

export function getNextWorkspaceMeta(workspace: BuilderWorkspaceMeta, market: Market): BuilderWorkspaceMeta | null {
  const workspaces = getBaseWorkspaces(market);
  if (!workspace.next) {
    return null;
  }

  return workspaces.find((item) => item.id === workspace.next) ?? null;
}

/** The element the workspace tabs control; BuilderPage renders it as the tabpanel. */
export const WORKSPACE_PANEL_ID = 'builder-workspace-panel';

export function workspaceTabId(workspace: BuilderWorkspace): string {
  return `builder-workspace-tab-${workspace}`;
}
